import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  IndianRupee, ReceiptText, Pill, Users, AlertTriangle, CalendarClock,
  Receipt, PackagePlus, PlusCircle, TrendingUp, TrendingDown, Wallet,
  CreditCard, QrCode, Activity, ShoppingCart, CalendarDays
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => format(d, "yyyy-MM-dd");
const COLORS = ["#0F5132", "#059669", "#10B981", "#34D399", "#6EE7B7", "#A7F3D0"];
const PAY_ICONS = { cash: Wallet, card: CreditCard, upi: QrCode, phonepe: IndianRupee, gpay: IndianRupee, scanner: QrCode };

export default function Dashboard() {
  const { user } = useAuth();
  const [range, setRange] = useState({ from: new Date(), to: new Date() });
  const [data, setData] = useState(null);
  const canSeeStaff = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    if (!range?.from || !range?.to) return;
    api
      .get("/dashboard/summary", { params: { from_date: fmtDate(range.from), to_date: fmtDate(range.to) } })
      .then(({ data }) => setData(data));
  }, [range]);

  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;

  const isToday = fmtDate(range.from) === fmtDate(new Date()) && fmtDate(range.to) === fmtDate(new Date());
  const rangeLabel = isToday
    ? `Today (${format(range.from, "dd MMM")})`
    : `${format(range.from, "dd MMM")} → ${format(range.to, "dd MMM yyyy")}`;
  const expAlerts = data.expiry_counts.expired + data.expiry_counts.critical + data.expiry_counts.high;

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header + date filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Overview</h1>
          <p className="text-sm text-slate-500 mt-1">{isToday ? "Live counter snapshot for today." : `Selected range: ${rangeLabel}`}</p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="dash-date-range-btn" className="h-11 bg-white border-slate-200 hover:border-emerald-500 font-mono-num">
              <CalendarDays className="w-4 h-4 mr-2 text-emerald-700" />
              {rangeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white" align="end">
            <Calendar
              data-testid="dash-calendar"
              mode="range"
              selected={range}
              onSelect={(r) => r && setRange(r)}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
              defaultMonth={range?.from}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3" data-testid="quick-actions">
        <Link to="/pos">
          <Button data-testid="quick-new-bill" className="w-full h-14 bg-emerald-900 hover:bg-emerald-800 text-white font-display font-bold text-base shadow-sm">
            <Receipt className="w-5 h-5 mr-2" /> New Bill
          </Button>
        </Link>
        {canSeeStaff ? (
          <>
            <Link to="/add-stock">
              <Button data-testid="quick-add-stock" variant="outline" className="w-full h-14 bg-white border-slate-200 hover:border-emerald-500 font-semibold">
                <PackagePlus className="w-5 h-5 mr-2 text-emerald-700" /> Add Stock
              </Button>
            </Link>
            <Link to="/add-medicine">
              <Button data-testid="quick-add-medicine" variant="outline" className="w-full h-14 bg-white border-slate-200 hover:border-emerald-500 font-semibold">
                <PlusCircle className="w-5 h-5 mr-2 text-emerald-700" /> Add Medicine
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Link to="/invoices">
              <Button variant="outline" className="w-full h-14 bg-white border-slate-200 hover:border-emerald-500 font-semibold">
                <ReceiptText className="w-5 h-5 mr-2 text-emerald-700" /> Invoices
              </Button>
            </Link>
            <Link to="/customers">
              <Button variant="outline" className="w-full h-14 bg-white border-slate-200 hover:border-emerald-500 font-semibold">
                <Users className="w-5 h-5 mr-2 text-emerald-700" /> Customers
              </Button>
            </Link>
          </>
        )}
      </div>

      {/* Expiry alert banner */}
      {expAlerts > 0 && (
        <Link to="/expire-soon">
          <div data-testid="expiry-alert-banner" className="p-4 rounded-xl border-2 border-red-300 bg-red-50 flex items-center gap-3 hover:bg-red-100/70 transition-colors cursor-pointer">
            <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-800 text-sm">Expiry Alert — action needed</div>
              <div className="text-xs text-red-700">
                {data.expiry_counts.expired > 0 && <span className="font-bold">{data.expiry_counts.expired} EXPIRED</span>}
                {data.expiry_counts.expired > 0 && (data.expiry_counts.critical > 0 || data.expiry_counts.high > 0) && " • "}
                {data.expiry_counts.critical > 0 && <span>{data.expiry_counts.critical} critical (≤30 days)</span>}
                {data.expiry_counts.critical > 0 && data.expiry_counts.high > 0 && " • "}
                {data.expiry_counts.high > 0 && <span>{data.expiry_counts.high} high alert (≤60 days)</span>}
                {" — sell before they expire"}
              </div>
            </div>
            <span className="text-xs font-semibold text-red-700 underline">View →</span>
          </div>
        </Link>
      )}

      {/* KPI cards with % change */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi tid="metric-range-revenue" icon={IndianRupee} label={isToday ? "Today's Sale" : "Range Sale"} value={fmtRs(data.range_revenue)} change={data.revenue_change_pct} changeLabel={isToday ? "vs yesterday" : "vs previous period"} />
        <Kpi tid="metric-range-bills" icon={ReceiptText} label="Bills" value={data.range_invoices} change={data.invoices_change_pct} changeLabel={isToday ? "vs yesterday" : "vs previous period"} />
        <Kpi tid="metric-inventory-value" icon={ShoppingCart} label="Stock Value" value={fmtRs(data.inventory_value)} sub="at purchase cost" />
        <Kpi tid="metric-customers" icon={Users} label="Customers" value={data.customers_count} sub={`${data.medicines_count} medicines`} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900">Sale Trend {isToday ? "" : `(${rangeLabel})`}</h3>
          <p className="text-xs text-slate-500">Last 7 days {isToday ? "" : "ke saath range ka comparison neeche"}</p>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={isToday ? data.last_7_days : data.days_series}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 }} />
                <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: "#0F5132" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Payment split */}
        <Card className="p-6 border-slate-200" data-testid="payment-split-card">
          <h3 className="font-display text-lg font-bold text-slate-900">Payment Split</h3>
          <p className="text-xs text-slate-500">{isToday ? "Today" : rangeLabel} — for cash drawer matching</p>
          <div className="mt-4 space-y-3">
            {data.payment_split.map((p) => {
              const Icon = PAY_ICONS[p.mode] || Wallet;
              const pct = data.range_revenue > 0 ? (p.amount / data.range_revenue) * 100 : 0;
              return (
                <div key={p.mode}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium text-slate-700">
                      <Icon className="w-4 h-4 text-emerald-700" />
                      <span className="uppercase">{p.mode}</span>
                      <span className="text-xs text-slate-400">({p.count})</span>
                    </span>
                    <span className="font-mono-num font-bold text-slate-900">{fmtRs(p.amount)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-emerald-700 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {data.payment_split.length === 0 && <p className="text-sm text-slate-400">No payments in this range.</p>}
          </div>
        </Card>
      </div>

      {/* Top meds + categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900">Top Medicines</h3>
          <p className="text-xs text-slate-500">By revenue</p>
          <div className="h-60 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_medicines} layout="vertical">
                <XAxis type="number" tick={{ fill: "#64748B", fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fill: "#0F172A", fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#0F5132" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200" data-testid="top-categories-card">
          <h3 className="font-display text-lg font-bold text-slate-900">Top Categories</h3>
          <p className="text-xs text-slate-500">Where sales come from</p>
          <div className="h-60 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.top_categories} dataKey="revenue" nameKey="category" outerRadius={80} label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                  {data.top_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtRs(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Agent performance (admin/manager) */}
      {canSeeStaff && (
        <Card className="p-6 border-slate-200" data-testid="agent-performance-card">
          <h3 className="font-display text-lg font-bold text-slate-900">Agent Performance</h3>
          <p className="text-xs text-slate-500">Bills created per agent — {isToday ? "today" : rangeLabel}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.agent_performance.map((a, i) => (
              <div key={a.name} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`agent-perf-${i}`}>
                <div className="w-10 h-10 rounded-full bg-emerald-900 text-white flex items-center justify-center font-bold">
                  {a.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.bills} bills</div>
                </div>
                <div className="font-mono-num font-bold text-emerald-900">{fmtRs(a.revenue)}</div>
              </div>
            ))}
            {data.agent_performance.length === 0 && <p className="text-sm text-slate-400">No sales in this range.</p>}
          </div>
        </Card>
      )}

      {/* Low stock reorder + expiring + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 border-slate-200" data-testid="low-stock-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <h3 className="font-display font-bold text-slate-900">Low Stock — Reorder</h3>
          </div>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-slate-500">All medicines are well stocked.</p>
          ) : (
            <div className="space-y-2">
              {data.low_stock.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{m.name}</div>
                    <div className="text-xs text-red-600 font-mono-num">{m.stock_strips} strips left</div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-mono-num shrink-0">+{m.suggested_order} order</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-slate-200" data-testid="expiring-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            <h3 className="font-display font-bold text-slate-900">Expiring (90 days)</h3>
          </div>
          {data.expiring_soon.length === 0 ? (
            <p className="text-sm text-slate-500">No medicines expiring.</p>
          ) : (
            <div className="space-y-2">
              {data.expiring_soon.slice(0, 6).map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{m.name}</div>
                    <div className="text-xs text-slate-500">Batch {m.batch_no || "—"}</div>
                  </div>
                  <div className={`font-mono-num text-xs font-bold shrink-0 ${m.alert === "expired" ? "text-red-700" : m.alert === "critical" ? "text-red-600" : "text-amber-700"}`}>
                    {m.days_left < 0 ? "EXPIRED" : `${m.days_left}d left`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-slate-200" data-testid="recent-activity-card">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-emerald-700" />
            <h3 className="font-display font-bold text-slate-900">Recent Activity</h3>
          </div>
          {data.recent_activity.length === 0 ? (
            <p className="text-sm text-slate-500">No activity yet.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_activity.map((a, i) => (
                <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-slate-100 last:border-0" data-testid={`activity-${i}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${a.type === "sale" ? "bg-emerald-100" : "bg-sky-100"}`}>
                    {a.type === "sale" ? <Receipt className="w-3.5 h-3.5 text-emerald-800" /> : <PackagePlus className="w-3.5 h-3.5 text-sky-800" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">{a.text}</div>
                    <div className="text-[10px] text-slate-400">{a.time ? new Date(a.time).toLocaleString("en-IN") : ""}</div>
                  </div>
                  {a.amount != null && <div className="font-mono-num text-xs font-bold text-emerald-900 shrink-0">{fmtRs(a.amount)}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, change, changeLabel, tid }) {
  const up = change >= 0;
  return (
    <Card data-testid={tid} className="p-5 border-slate-200">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-mono-num text-2xl lg:text-3xl font-extrabold text-slate-900 mt-1.5 truncate">{value}</div>
          {change !== undefined && (
            <div className={`flex items-center gap-1 text-xs font-semibold mt-1 ${up ? "text-emerald-700" : "text-red-600"}`}>
              {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {up ? "+" : ""}{change}% <span className="text-slate-400 font-normal">{changeLabel}</span>
            </div>
          )}
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-emerald-700" />
        </div>
      </div>
    </Card>
  );
}
